/**
 * R96 · Settings › Task Categories
 * ------------------------------------------------------------------
 * Admin-facing CRUD for the workspace's Task Category dropdown so
 * category values no longer need a DB tweak. Talks to:
 *   GET    /workspaces/{ws}/task-categories?include_archived=true
 *   POST   /workspaces/{ws}/task-categories
 *   PATCH  /task-categories/{cid}
 *   DELETE /task-categories/{cid}      (soft archive)
 * Cascade-renames the denormalised `task_category_name` on tasks so
 * live filters keep working without a bg job.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Archive, RotateCw, Check, X, ArchiveRestore } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { apiErrorMessage } from "@/lib/apiError";

export default function TaskCategoriesSettings() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name}

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${activeWorkspaceId}/task-categories?include_archived=true`);
      setRows(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load categories")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const create = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/task-categories`, { name });
      setNewName("");
      toast.success(`Added "${name}"`);
      await load();
    } catch (err) { toast.error(apiErrorMessage(err, "Create failed")); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return;
    try {
      await api.patch(`/task-categories/${editing.id}`, { name });
      toast.success("Renamed · running tasks updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(apiErrorMessage(err, "Rename failed")); }
  };

  const archive = async (row) => {
    if (!window.confirm(`Archive "${row.name}"? Existing tasks keep their category label but new tasks can't pick it.`)) return;
    try {
      await api.delete(`/task-categories/${row.id}`);
      toast.success("Category archived");
      await load();
    } catch (err) { toast.error(apiErrorMessage(err, "Archive failed")); }
  };

  const restore = async (row) => {
    try {
      await api.post(`/task-categories/${row.id}/restore`);
      toast.success("Restored");
      await load();
    } catch (err) { toast.error(apiErrorMessage(err, "Restore failed")); }
  };

  const visible = rows
    .map((r) => ({ ...r, archived: r.is_active === false || !!r.archived }))
    .filter((r) => (showArchived ? true : !r.archived))
    .filter((r) => (q.trim() ? (r.name || "").toLowerCase().includes(q.trim().toLowerCase()) : true))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="space-y-4 p-4" data-testid="task-categories-settings-page">
      <PageHeader
        title="Task Categories"
        description="Edit the values available in the Task Category dropdown across every project, meeting and recurring task."
      />

      <form onSubmit={create} className="flex items-center gap-2 flex-wrap" data-testid="task-cat-create-form">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name…"
          className="h-9 w-64 rounded-none text-sm"
          data-testid="task-cat-new-name"
        />
        <Button type="submit" className="rounded-none h-9" disabled={busy || !newName.trim()} data-testid="task-cat-create-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          Add category
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="Filter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 w-56 rounded-none text-xs"
            data-testid="task-cat-search"
          />
          <label className="text-xs inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="task-cat-show-archived"
            />
            Show archived
          </label>
          <Button variant="outline" size="sm" className="rounded-none h-9" onClick={load} disabled={loading} data-testid="task-cat-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </form>

      {loading && rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading categories…</div>
      ) : visible.length === 0 ? (
        <EmptyState title="No categories yet" description="Add your first Task Category — used to slice reports and gate recurring templates." icon={Plus} testid="task-cat-empty" />
      ) : (
        <div className="border border-border" data-testid="task-cat-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b border-border text-[10px] uppercase tracking-widest">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-28">Status</th>
                <th className="text-right px-3 py-2 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const isEditing = editing?.id === row.id;
                return (
                  <tr key={row.id} className="border-b border-border/60" data-testid={`task-cat-row-${row.id}`}>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editing.name}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }}
                          className="h-8 rounded-none text-sm"
                          data-testid={`task-cat-edit-input-${row.id}`}
                        />
                      ) : (
                        <span className="font-medium" data-testid={`task-cat-name-${row.id}`}>{row.name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.archived
                        ? <span className="chip text-[10px] border-muted-foreground/30 text-muted-foreground">archived</span>
                        : <span className="chip text-[10px] border-emerald-500/40 text-emerald-700">active</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="inline-flex gap-1">
                          <Button size="sm" className="rounded-none h-7 text-xs" onClick={saveEdit} data-testid={`task-cat-save-${row.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setEditing(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                            onClick={() => setEditing({ id: row.id, name: row.name })}
                            data-testid={`task-cat-edit-${row.id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Rename
                          </Button>
                          {row.archived ? (
                            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                              onClick={() => restore(row)}
                              data-testid={`task-cat-restore-${row.id}`}>
                              <ArchiveRestore className="w-3 h-3 mr-1" /> Restore
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-rose-700 border-rose-300"
                              onClick={() => archive(row)}
                              data-testid={`task-cat-archive-${row.id}`}>
                              <Archive className="w-3 h-3 mr-1" /> Archive
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Renames cascade to the denormalised <code className="text-[10px]">task_category_name</code> on every task so
        filters stay accurate immediately — no background job required.
      </p>
    </div>
  );
}
